import { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import type { MessageDescriptor } from 'react-intl';

import compact from 'lodash/compact';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import LinkIcon from '@mui/icons-material/Link';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';
import StarRateIcon from '@mui/icons-material/StarRate';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import {
  toTimezone,
  timeFormat,
  forceString,
  weekDateFormat,
} from 'src/utils/datetime';
import { locale } from 'src/config';
import SearchFilterBar, {
  type QuickFiltersType,
} from 'src/components/SearchFilterBar';
import Dropdown from 'src/components/Dropdown';
import TextInput from 'src/components/TextInput';
import TimeInput from 'src/components/TimeInput';
import DataTable from 'src/components/DataTable';
import PageLayout from 'src/components/PageLayout';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { formatNumber, toParsableNumber } from 'src/utils/standards';
import JobLink from 'src/components/JobLink';
import ProjectLink from 'src/components/ProjectLink';

import {
  useImportTimeTracking,
  useGetCandidateJobShifts,
  useUpdateSaveTimeTracking,
  CandidateJobShiftFiltersType,
  useGetDeviationReasonOptions,
  useUpdateApproveTimeTracking,
  type UserRecordType,
} from './api';
import messages from './messages';
import {
  useCreateCancellationNotices,
  useUpdateWithdrawCancellationNotices,
} from '../ProjectDetailsPage/api';
import CheckTimeData from './components/CheckTimeData';
import { ImportButton } from './components/ImportButton';
import DateRangeModal from '../../components/DateRangeModal';
import NumberInputField from './components/NumberInputField';
import { CancellationData } from '../ProjectDetailsPage/components/JobCalendar';
import { REJECT_REASON } from '../ProjectDetailsPage/api/queries/useGetRejectReason';
import CancellationModal from '../ProjectDetailsPage/components/JobDetails/components/CancellationModal/CancellationModal';
import PaymentSummaryFormModal, {
  DECIMALS,
  WORKED_HOURS_WARNING,
  WORKED_MINUTES_LIMIT,
} from './components/PaymentSummaryFormModal';

const navTabs: Array<{
  label: string;
  value: UCM.CandidateJobShiftStatusType;
}> = [
  {
    value: 'upcoming',
    label: 'Upcoming',
  },
  {
    value: 'check_time_available',
    label: 'Pending',
  },
  {
    value: 'submitted',
    label: 'Submitted',
  },
  {
    value: 'approved',
    label: 'Approved',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
  },
  {
    value: 'done',
    label: 'Done',
  },
];

const initialFilters: CandidateJobShiftFiltersType = {
  job_id: '',
  user_id: '',
  project_id: '',
  status: 'check_time_available',
  from_date: moment().startOf('month').startOf('day'),
  to_date: moment().endOf('month').startOf('day'),
};

const textSalarySx = {
  textDecoration: 'underline',
  cursor: 'pointer',
};

const breakTakenSx = { input: { textAlign: 'center' } };
const deviationReasonSx = { maxWidth: 64 };

export default function TimeTrackingPage() {
  const { formatMessage } = useIntl();

  const [usersRecords, setUsersRecords] = useState<
    Record<string, UserRecordType>
  >({});
  const [editCancellation, setEditCancellation] =
    useState<Partial<UCM.CancellationType> | null>(null);
  const [filters, setFilters] =
    useState<CandidateJobShiftFiltersType>(initialFilters);
  const [activeTab, setActiveTab] = useState(filters.status);
  const [params, setParams] = useState<Record<string, number>>({
    page: 1,
  });
  const [openCancellation, setOpenCancellation] = useState(false);
  const [selected, setSelected] = useState<UCM.CandidateJobShiftType | null>(
    null,
  );
  const [cancellationReason, setCancellationReason] = useState<string | null>(
    null,
  );

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [paymentRecord, setPaymentRecord] =
    useState<UCM.CandidateJobShiftType | null>(null);

  const booleanOptions = useRef([
    { value: 'true', label: formatMessage(messages.yesLabel) },
    { value: 'false', label: formatMessage(messages.noLabel) },
  ]).current;

  const { mutate: withdrawCancellationNotices } =
    useUpdateWithdrawCancellationNotices({});
  const { mutate: importTimeTracking, isPending: isImporting } =
    useImportTimeTracking();
  const { mutate: cancelTimeTracking, isPending: isCancelling } =
    useCreateCancellationNotices({});
  const { mutate: saveTimeTracking } = useUpdateSaveTimeTracking();
  const { mutate: approveTimeTracking } = useUpdateApproveTimeTracking();
  const { data: deviationReasonOptions } = useGetDeviationReasonOptions();
  const { data: candidateJobShifts, isFetching: isFetchingCandidateJobShifts } =
    useGetCandidateJobShifts(params, filters);

  const searchFilters = useMemo(() => {
    return [
      {
        id: 'user_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchUidPlaceholder),
      },
      {
        id: 'project_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchPidPlaceholder),
      },
      {
        id: 'job_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchJidPlaceholder),
      },
    ];
  }, []);

  const handleChangeRecord = useCallback(
    (
      prevRecord: UCM.CandidateJobShiftType,
      nextRecord: Partial<UserRecordType>,
    ) => {
      const localData = usersRecords[prevRecord.uuid] ?? {};
      const existingRecord = candidateJobShifts?.data.find(
        (record) => record.id === prevRecord.id,
      );

      const dataToSave = {
        ...existingRecord,
        ...localData,
        ...nextRecord,
      };

      setUsersRecords((prevState) => ({
        ...prevState,
        [prevRecord.uuid]: dataToSave,
      }));
    },
    [usersRecords, candidateJobShifts?.data],
  );

  const formatBooleanLabel = useCallback(
    (
      value: boolean | null | undefined,
      yesLabel: MessageDescriptor,
      noLabel: MessageDescriptor,
    ) => {
      return value != null ? formatMessage(value ? yesLabel : noLabel) : '';
    },
    [],
  );

  const handleCheckInChange = useCallback(
    (record: UCM.CandidateJobShiftType, key: keyof UserRecordType) =>
      (newCheckIn: Moment | null) => {
        const checkOut = toTimezone(
          usersRecords[record.uuid]?.checkOut
            ? usersRecords[record.uuid]?.checkOut
            : record.checkOut,
        );

        if (!newCheckIn) {
          return handleChangeRecord(record, {
            [key]: newCheckIn,
            checkOut,
          });
        }

        if (!newCheckIn.isValid()) {
          return handleChangeRecord(record, {
            [key]: null,
            checkOut,
          });
        }

        const startTime = toTimezone(record.startTime);
        const endTime = toTimezone(record.endTime);

        if (!startTime || !endTime) return;

        newCheckIn = newCheckIn
          .clone()
          .year(startTime.year())
          .month(startTime.month())
          .date(startTime.date());

        // Adjust if the new start time is incorrect after both start & end time
        if (newCheckIn.isAfter(startTime) && newCheckIn.isAfter(endTime)) {
          newCheckIn = newCheckIn.subtract(1, 'days');
        }

        let currEndTime =
          toTimezone(usersRecords[record.uuid]?.checkOut) || null;

        // Ensure currEndTime is not null
        if (currEndTime) {
          currEndTime = currEndTime.clone();

          while (newCheckIn.isAfter(currEndTime)) {
            currEndTime.add(1, 'days');
          }

          while (
            currEndTime.diff(newCheckIn, 'minutes') > WORKED_MINUTES_LIMIT
          ) {
            currEndTime.subtract(1, 'days');
          }

          if (newCheckIn.isAfter(currEndTime)) {
            newCheckIn.add(1, 'days');
          }
        }

        handleChangeRecord(record, {
          [key]: newCheckIn,
          checkOut,
        });
      },
    [usersRecords, handleChangeRecord],
  );

  const handleCheckOutChange = useCallback(
    (record: UCM.CandidateJobShiftType, key: keyof UserRecordType) =>
      (newCheckOut: Moment | null) => {
        const checkIn = toTimezone(
          usersRecords[record.uuid]?.checkIn
            ? usersRecords[record.uuid]?.checkIn
            : record.checkIn,
        );

        if (!newCheckOut) {
          return handleChangeRecord(record, {
            [key]: newCheckOut,
            checkIn,
          });
        }

        if (!newCheckOut.isValid()) {
          return handleChangeRecord(record, {
            [key]: null,
            checkIn,
          });
        }

        const startTime = toTimezone(record.startTime);
        const endTime = toTimezone(record.endTime);

        if (!startTime || !endTime) return;

        // Clone newEndTime before modifying
        newCheckOut = newCheckOut
          .clone()
          .year(startTime.year())
          .month(startTime.month())
          .date(startTime.date());

        if (newCheckOut.isBefore(startTime) && newCheckOut.isBefore(endTime)) {
          newCheckOut = newCheckOut.add(1, 'days');
        }

        let currStartTime =
          toTimezone(usersRecords[record.uuid]?.checkIn) || null;

        if (currStartTime) {
          currStartTime = currStartTime.clone();

          while (newCheckOut.isBefore(currStartTime)) {
            newCheckOut.add(1, 'days');
          }

          while (
            newCheckOut.diff(currStartTime, 'minutes') > WORKED_MINUTES_LIMIT
          ) {
            newCheckOut.subtract(1, 'days');
          }

          if (newCheckOut.isBefore(currStartTime)) {
            newCheckOut.add(1, 'days');
          }
        }

        handleChangeRecord(record, {
          [key]: newCheckOut,
          checkIn,
        });
      },
    [usersRecords, handleChangeRecord],
  );

  const getClientCheckIn = useCallback(
    (record: UCM.CandidateJobShiftType) => {
      const changedRecord = usersRecords[record.uuid];
      const checkIn = toTimezone(
        changedRecord ? changedRecord.clientCheckin : record.clientCheckin,
      );
      return checkIn?.millisecond(0).second(0);
    },
    [usersRecords],
  );

  const getClientCheckOut = useCallback(
    (record: UCM.CandidateJobShiftType) => {
      const changedRecord = usersRecords[record.uuid];
      const checkOut = toTimezone(
        changedRecord ? changedRecord.clientCheckout : record.clientCheckout,
      );
      return checkOut?.millisecond(0).second(0);
    },
    [usersRecords],
  );

  const filterDeviationReasons = useCallback(
    (record: UCM.CandidateJobShiftType) => {
      const clientCheckIn = getClientCheckIn(record);
      const clientCheckOut = getClientCheckOut(record);

      const userCheckIn = toTimezone(record.checkIn);
      const userCheckOut = toTimezone(record.checkOut);

      const plannedCheckIn = toTimezone(record.startTime);
      const plannedCheckOut = toTimezone(record.endTime);

      const actualCheckIn = clientCheckIn ?? userCheckIn;
      const actualCheckOut = clientCheckOut ?? userCheckOut;

      const isLate = (actual: Moment | null, scheduled: Moment | null) =>
        actual?.isAfter(scheduled);
      const isEarly = (actual: Moment | null, scheduled: Moment | null) =>
        actual?.isBefore(scheduled);
      const isOnTime = (actual: Moment | null, scheduled: Moment | null) =>
        actual?.isSame(scheduled);

      const filteredOptions = {
        checkIn: [] as Array<UCM.DeviationReasonOptionType> | null | undefined,
        checkOut: [] as Array<UCM.DeviationReasonOptionType> | null | undefined,
      };

      if (isLate(actualCheckIn, plannedCheckIn)) {
        filteredOptions.checkIn = deviationReasonOptions?.checkIn?.filter(
          (option) => option.reason === 'check_in_late',
        );
      } else if (isEarly(actualCheckIn, plannedCheckIn)) {
        filteredOptions.checkIn = deviationReasonOptions?.checkIn?.filter(
          (option) => option.reason === 'check_in_early',
        );
      } else if (isOnTime(actualCheckIn, plannedCheckIn)) {
        filteredOptions.checkIn = null;
        const deviationReason =
          usersRecords[record.uuid]?.checkinDeviationReason ??
          record.checkinDeviationReason ??
          '';
        if (deviationReason) {
          setTimeout(() => {
            handleChangeRecord(record, {
              checkinDeviationReason: '',
            });
          }, 0);
        }
      }

      if (isLate(actualCheckOut, plannedCheckOut)) {
        filteredOptions.checkOut = deviationReasonOptions?.checkOut?.filter(
          (option) => option.reason === 'check_out_late',
        );
      } else if (isEarly(actualCheckOut, plannedCheckOut)) {
        filteredOptions.checkOut = deviationReasonOptions?.checkOut?.filter(
          (option) => option.reason === 'check_out_early',
        );
      } else if (isOnTime(actualCheckOut, plannedCheckOut)) {
        filteredOptions.checkOut = null;
        const deviationReason =
          usersRecords[record.uuid]?.checkoutDeviationReason ??
          record.checkoutDeviationReason ??
          '';
        if (deviationReason) {
          setTimeout(() => {
            handleChangeRecord(record, {
              checkoutDeviationReason: '',
            });
          }, 0);
        }
      }
      return filteredOptions;
    },
    [
      getClientCheckIn,
      getClientCheckOut,
      handleChangeRecord,
      deviationReasonOptions,
      usersRecords,
    ],
  );

  const renderField = useCallback(
    (record: UCM.CandidateJobShiftType, field: keyof UserRecordType) => {
      const recordId = record.uuid;
      const existingValue = record[field]?.toString() ?? '';
      const foundValue = usersRecords[recordId]?.[field]?.toString();

      const formattedValue = formatNumber(foundValue ?? existingValue, locale);

      return (
        <NumberInputField
          sx={breakTakenSx}
          value={formattedValue}
          onBlurChange={(
            _: React.FocusEvent<HTMLInputElement>,
            value: string,
          ) => {
            const parsedValue = parseFloat(toParsableNumber(value, locale));
            handleChangeRecord(record, {
              [field]: parsedValue,
            });
          }}
        />
      );
    },
    [usersRecords, handleChangeRecord],
  );

  const handleCancelAndUnCancel = useCallback(
    (record: UCM.CandidateJobShiftType, type: 'cancel' | 'unCancel') => {
      setSelected(record);
      setOpenCancellation(true);

      if (type === 'cancel') {
        setCancellationReason(REJECT_REASON.CANCELLATION_NOTICE);
        setEditCancellation(null);
      } else if (type === 'unCancel') {
        setCancellationReason(REJECT_REASON.RESET_TIME_TRACKING);
        setEditCancellation({
          id: record.cancellation?.id ?? undefined,
          uuid: record.cancellation?.uuid ?? undefined,
        });
      }
    },
    [],
  );

  const handleSelectSalary = useCallback(
    (record: UCM.CandidateJobShiftType) => {
      setPaymentRecord(record);
      setOpenPaymentModal(true);
    },
    [],
  );

  const handleClosePaymentModal = useCallback(() => {
    setOpenPaymentModal(false);
    setPaymentRecord(null);
  }, []);

  const fixedColumns = useMemo(() => {
    return [
      {
        id: 'projectId',
        align: 'center',
        label: formatMessage(messages.projectColumnLabel),
        render: (
          projectId: number,
          { projectUuid }: UCM.CandidateJobShiftType,
        ) => (
          <ProjectLink
            projectUuid={projectUuid}
            label={projectId}
            target="_blank"
          />
        ),
      },
      {
        id: 'jobId',
        align: 'center',
        label: formatMessage(messages.jobColumnLabel),
        render: (jobId: number, { projectUuid }: UCM.CandidateJobShiftType) => (
          <JobLink jobId={jobId} projectUuid={projectUuid} target="_blank" />
        ),
      },
      {
        id: 'student',
        label: formatMessage(messages.studentColumnLabel),
        render: (_: Object, { user }: UCM.CandidateJobShiftType) => {
          return (
            <Typography variant="inherit">
              {user.id} - {user.firstName} {user.lastName}
            </Typography>
          );
        },
      },
      {
        id: 'break',
        align: 'center',
        label: formatMessage(messages.breakColumnLabel),
        render: (_: Object, { isBreakPaid }: UCM.CandidateJobShiftType) => {
          return (
            <Typography variant="inherit">
              {formatBooleanLabel(
                isBreakPaid,
                messages.breakPaidLabel,
                messages.breakUnpaidLabel,
              )}
            </Typography>
          );
        },
      },
      {
        id: 'isBreakTaken',
        label: formatMessage(messages.breakTakenColumnLabel),
        align: 'center',
        render: (isBreakTaken: boolean, record: UCM.CandidateJobShiftType) => {
          const foundRecord = usersRecords[record.uuid];

          const value = foundRecord?.isBreakTaken ?? isBreakTaken;

          return (
            <Dropdown
              fullWidth={false}
              value={value.toString()}
              options={booleanOptions}
              onChange={(event) =>
                handleChangeRecord(record, {
                  isBreakTaken: event.target.value === 'true',
                })
              }
            />
          );
        },
      },
      {
        id: 'minWorkingHours',
        align: 'center',
        label: formatMessage(messages.minimumHoursColumnLabel),
        render: (minWorkingHours: number | null) => (
          <Typography variant="inherit">
            {minWorkingHours
              ? formatNumber(minWorkingHours, locale, true, DECIMALS)
              : '-'}
          </Typography>
        ),
      },
      {
        id: 'shift',
        minWidth: 80,
        align: 'center',
        label: formatMessage(messages.shiftColumnLabel),
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <>
            <Typography variant="inherit">
              {forceString(record.startTime, weekDateFormat).toUpperCase()}
            </Typography>

            <Typography variant="inherit">
              {`${forceString(record.startTime, timeFormat)} - ${forceString(
                record.endTime,
                timeFormat,
              )}`}
            </Typography>
          </>
        ),
      },
      {
        id: 'isContractSigned',
        label: formatMessage(messages.contractColumnLabel),
        align: 'center',
        render: (isContractSigned: boolean | null) => (
          <Typography variant="inherit">
            {isContractSigned != null
              ? formatBooleanLabel(
                  isContractSigned,
                  messages.signedLabel,
                  messages.notSignedLabel,
                )
              : '-'}
          </Typography>
        ),
      },
      {
        id: 'isBriefingConfirmed',
        label: formatMessage(messages.briefingColumnLabel),
        align: 'center',
        render: (isBriefingConfirmed: boolean | null) => (
          <Typography variant="inherit">
            {isBriefingConfirmed != null
              ? formatBooleanLabel(
                  isBriefingConfirmed,
                  messages.readLabel,
                  messages.notReadLabel,
                )
              : '-'}
          </Typography>
        ),
      },
    ];
  }, [usersRecords]);

  const upcomingColumns = useMemo(() => {
    const excludedColumns = ['isBreakTaken'];

    return [
      ...fixedColumns.filter((column) => !excludedColumns.includes(column.id)),
      {
        id: 'actions',
        label: '',
        width: 0,
        align: 'center',
        whiteSpace: 'nowrap',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <Tooltip title={formatMessage(messages.cancelHint)}>
            <IconButton
              onClick={() => handleCancelAndUnCancel(record, 'cancel')}
            >
              <CloseOutlinedIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        ),
      },
    ];
  }, []);

  const pendingColumns = useMemo(() => {
    return [
      ...fixedColumns,
      {
        id: 'checkIn',
        label: formatMessage(messages.checkInColumnLabel),
        align: 'center',
        render: (checkIn: string | null, record: UCM.CandidateJobShiftType) => {
          const changedRecord = usersRecords[record.uuid];

          return (
            <TimeInput
              name="checkIn"
              value={toTimezone(
                changedRecord ? changedRecord.checkIn : checkIn,
              )}
              onChange={(newValue) =>
                handleCheckInChange(
                  record,
                  'checkIn',
                )(newValue ?? toTimezone(record.checkIn))
              }
            />
          );
        },
      },
      {
        id: 'checkOut',
        label: formatMessage(messages.checkOutColumnLabel),
        align: 'center',
        render: (
          checkOut: string | null,
          record: UCM.CandidateJobShiftType,
        ) => {
          const changedRecord = usersRecords[record.uuid];

          return (
            <TimeInput
              name="checkOut"
              value={toTimezone(
                changedRecord ? changedRecord.checkOut : checkOut,
              )}
              onChange={handleCheckOutChange(record, 'checkOut')}
            />
          );
        },
      },
      {
        id: 'adminComment',
        align: 'center',
        label: formatMessage(messages.adminCommentColumnLabel),
        render: (
          adminComment: string | null,
          record: UCM.CandidateJobShiftType,
        ) => {
          const value =
            usersRecords[record.uuid]?.adminComment ?? (adminComment || '');

          return (
            <TextInput
              fullWidth
              multiline
              name="adminComment"
              value={value}
              onChange={(event) =>
                handleChangeRecord(record, { adminComment: event.target.value })
              }
            />
          );
        },
      },
      {
        id: 'actions',
        label: '',
        width: 0,
        align: 'center',
        whiteSpace: 'nowrap',
        render: (_: Object, record: UCM.CandidateJobShiftType) => {
          const userRecord = usersRecords?.[record.uuid];

          const disabled =
            !userRecord ||
            (record.checkIn == null && userRecord.checkIn == null) ||
            (record.checkOut == null && userRecord.checkOut == null) ||
            userRecord.isBreakTaken == null;

          return (
            <Grid display="flex" alignItems="center">
              <ConfirmationDialog
                onConfirm={() => handleSave(record.uuid, userRecord)}
                message={formatMessage(messages.saveTimeTrackingConfirmMessage)}
              >
                {(handleOpen) => (
                  <Tooltip title={formatMessage(messages.submitHint)}>
                    <div>
                      <IconButton
                        disabled={disabled}
                        onClick={handleOpen}
                        style={{ color: disabled ? 'gray' : 'inherit' }}
                      >
                        <SaveOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </Tooltip>
                )}
              </ConfirmationDialog>

              <Tooltip title={formatMessage(messages.cancelHint)}>
                <IconButton
                  onClick={() => handleCancelAndUnCancel(record, 'cancel')}
                >
                  <CloseOutlinedIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </Grid>
          );
        },
      },
    ];
  }, [
    usersRecords,
    handleChangeRecord,
    handleCheckInChange,
    handleCheckOutChange,
  ]);

  const submittedColumns = useMemo(() => {
    const excludedColumns = [
      'isBreakTaken',
      'isContractSigned',
      'isBriefingConfirmed',
    ];

    return [
      ...fixedColumns.filter((column) => !excludedColumns.includes(column.id)),
      {
        id: 'userCheckTime',
        label: formatMessage(messages.userCheckTimeColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <CheckTimeData
            checkinContent={
              <Tooltip arrow title={forceString(record.checkIn)}>
                <Typography variant="inherit">
                  {record.checkIn != null
                    ? forceString(record.checkIn, timeFormat)
                    : '-'}
                </Typography>
              </Tooltip>
            }
            checkoutContent={
              <Tooltip arrow title={forceString(record.checkOut)}>
                <Typography variant="inherit">
                  {record.checkOut != null
                    ? forceString(record.checkOut, timeFormat)
                    : '-'}
                </Typography>
              </Tooltip>
            }
          />
        ),
      },
      {
        id: 'clientCheckTime',
        label: formatMessage(messages.clientCheckTimeColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => {
          const clientCheckin = getClientCheckIn(record) ?? null;
          const clientCheckout = getClientCheckOut(record) ?? null;

          return (
            <CheckTimeData
              divider={
                clientCheckin?.isValid() || clientCheckout?.isValid() ? (
                  <Tooltip
                    title={formatMessage(messages.dateShifterHint)}
                    placement="right"
                    arrow
                  >
                    <ButtonGroup size="small" fullWidth>
                      <Button
                        onClick={() => {
                          if (clientCheckin) clientCheckin.subtract(1, 'days');
                          if (clientCheckout)
                            clientCheckout.subtract(1, 'days');

                          handleChangeRecord(record, {
                            clientCheckin,
                            clientCheckout,
                          });
                        }}
                      >
                        <ArrowLeftIcon fontSize="small" />
                      </Button>
                      <Button
                        onClick={() => {
                          if (clientCheckin) clientCheckin.add(1, 'days');
                          if (clientCheckout) clientCheckout.add(1, 'days');
                          handleChangeRecord(record, {
                            clientCheckin,
                            clientCheckout,
                          });
                        }}
                      >
                        <ArrowRightIcon fontSize="small" />
                      </Button>
                    </ButtonGroup>
                  </Tooltip>
                ) : undefined
              }
              checkinContent={
                <Stack alignItems="center" justifyContent="center">
                  <Typography variant="caption" color="textSecondary">
                    {clientCheckin && forceString(clientCheckin)}
                  </Typography>

                  <TimeInput
                    name="checkIn"
                    value={clientCheckin}
                    onChange={handleCheckInChange(record, 'clientCheckin')}
                  />
                </Stack>
              }
              checkoutContent={
                <Stack alignItems="center" justifyContent="center">
                  <Typography variant="caption" color="textSecondary">
                    {forceString(clientCheckout)}
                  </Typography>

                  <TimeInput
                    name="checkOut"
                    value={clientCheckout}
                    onChange={handleCheckOutChange(record, 'clientCheckout')}
                  />
                </Stack>
              }
            />
          );
        },
      },
      {
        id: 'plannedShift',
        label: formatMessage(messages.plannedShiftColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <CheckTimeData
            checkinContent={
              <Tooltip arrow title={forceString(record.startTime)}>
                <Typography variant="inherit">
                  {forceString(record.startTime, timeFormat)}
                </Typography>
              </Tooltip>
            }
            checkoutContent={
              <Tooltip arrow title={forceString(record.endTime)}>
                <Typography variant="inherit">
                  {forceString(record.endTime, timeFormat)}
                </Typography>
              </Tooltip>
            }
          />
        ),
      },
      {
        id: 'checkDeviation',
        label: formatMessage(messages.checkDeviationColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <CheckTimeData
            checkinContent={() => {
              if (record.checkinDeviation == null) return '-';
              return formatDeviation(
                record.checkinDeviation,
                record.checkinStatus,
              );
            }}
            checkoutContent={() => {
              if (record.checkoutDeviation == null) return '-';
              return formatDeviation(
                record.checkoutDeviation,
                record.checkoutStatus,
              );
            }}
          />
        ),
      },
      {
        id: 'isCheckAutoApproved',
        label: formatMessage(messages.isCheckAutoApprovedColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <CheckTimeData
            checkinContent={() => {
              if (record.isCheckinAutoApproved == null) return '-';
              return record.isCheckinAutoApproved ? (
                <CheckOutlinedIcon fontSize="small" color="success" />
              ) : (
                <CloseOutlinedIcon fontSize="small" color="error" />
              );
            }}
            checkoutContent={() => {
              if (record.isCheckoutAutoApproved == null) return '-';
              return record.isCheckoutAutoApproved ? (
                <CheckOutlinedIcon fontSize="small" color="success" />
              ) : (
                <CloseOutlinedIcon fontSize="small" color="error" />
              );
            }}
          />
        ),
      },
      {
        id: 'deviationReason',
        label: formatMessage(messages.deviationReasonColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => {
          const filteredDeviationReasons = filterDeviationReasons(record);

          return (
            <CheckTimeData
              checkinContent={() => {
                const filteredOptions = filteredDeviationReasons.checkIn;
                if (!filteredOptions?.length) return '-';
                const reasonCode =
                  usersRecords[record.uuid]?.checkinDeviationReason ??
                  record.checkinDeviationReason ??
                  '';
                const isCodeCovered = filteredOptions.some(
                  (reasonOption) => reasonOption.value === reasonCode,
                );
                return (
                  <Dropdown
                    fullWidth={false}
                    sx={deviationReasonSx}
                    options={filteredOptions}
                    value={isCodeCovered ? reasonCode : ''}
                    onChange={(event) =>
                      handleChangeRecord(record, {
                        checkinDeviationReason: event.target.value as string,
                      })
                    }
                  />
                );
              }}
              checkoutContent={() => {
                const filteredOptions = filteredDeviationReasons.checkOut;
                if (!filteredOptions?.length) return '-';
                const reasonCode =
                  usersRecords[record.uuid]?.checkoutDeviationReason ??
                  record.checkoutDeviationReason ??
                  '';
                const isCodeCovered = filteredOptions.some(
                  (reasonOption) => reasonOption.value === reasonCode,
                );
                return (
                  <Dropdown
                    fullWidth={false}
                    sx={deviationReasonSx}
                    options={filteredOptions}
                    value={isCodeCovered ? reasonCode : ''}
                    onChange={(event) =>
                      handleChangeRecord(record, {
                        checkoutDeviationReason: event.target.value as string,
                      })
                    }
                  />
                );
              }}
            />
          );
        },
      },
      {
        id: 'checkLocation',
        label: formatMessage(messages.checkLocationColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <CheckTimeData
            checkinContent={() => {
              if (
                !record.checkinLocationLatitude ||
                !record.checkinLocationLongitude
              ) {
                return '-';
              }
              return (
                <Tooltip
                  title={formatMessage(messages.openInGoogleMapsHint)}
                  arrow
                >
                  <Link
                    target="__blank"
                    href={`https://www.google.com/maps/search/?api=1&query=${
                      record.checkinLocationLatitude
                    },${record.checkinLocationLongitude}`}
                  >
                    <LinkIcon fontSize="small" />
                  </Link>
                </Tooltip>
              );
            }}
            checkoutContent={() => {
              if (
                !record.checkoutLocationLatitude ||
                !record.checkoutLocationLongitude
              ) {
                return '-';
              }
              return (
                <Tooltip
                  title={formatMessage(messages.openInGoogleMapsHint)}
                  arrow
                >
                  <Link
                    target="__blank"
                    href={`https://www.google.com/maps/search/?api=1&query=${
                      record.checkoutLocationLatitude
                    },${record.checkoutLocationLongitude}`}
                  >
                    <LinkIcon fontSize="small" />
                  </Link>
                </Tooltip>
              );
            }}
          />
        ),
      },
      {
        id: 'clientUserRating',
        label: formatMessage(messages.userRatingColumnLabel),
        align: 'center',
        render: (clientUserRating: number | null) => {
          if (!clientUserRating) {
            return <Typography variant="inherit">-</Typography>;
          }
          return (
            <Box display="flex" alignItems="flex-end" justifyContent="center">
              <Typography variant="inherit">{clientUserRating}</Typography>
              <StarRateIcon fontSize="small" />
            </Box>
          );
        },
      },
      {
        id: 'isBreakTaken',
        label: formatMessage(messages.breakColumnLabel),
        align: 'center',
        render: (isBreakTaken: boolean, record: UCM.CandidateJobShiftType) => {
          const foundRecord = usersRecords[record.uuid];

          const value = foundRecord?.isBreakTaken ?? isBreakTaken;

          return (
            <div style={{ textAlign: 'center' }}>
              <Typography variant="inherit">
                {value !== null
                  ? formatBooleanLabel(
                      value,
                      messages.yesLabel,
                      messages.noLabel,
                    )
                  : '-'}
              </Typography>
              {renderField(record, 'breakHours')}
            </div>
          );
        },
      },
      {
        id: 'noCodeReason',
        label: formatMessage(messages.noCodeReasonColumnLabel),
        align: 'center',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <CheckTimeData
            checkinContent={
              <Typography variant="inherit">
                {record.checkinNoCodeReason ?? '-'}
              </Typography>
            }
            checkoutContent={
              <Typography variant="inherit">
                {record.checkoutNoCodeReason ?? '-'}
              </Typography>
            }
          />
        ),
      },
      {
        id: 'salary',
        label: formatMessage(messages.salaryColumnLabel),
        align: 'center',
        render: (_: string | Object, record: UCM.CandidateJobShiftType) => {
          const salaryValue =
            record.salary == null && record.wageBonus == null
              ? ''
              : (record.salary ?? 0) + (record.wageBonus ?? 0);

          return (
            <>
              <Typography
                variant="inherit"
                sx={textSalarySx}
                onClick={() => handleSelectSalary(record)}
              >
                {salaryValue
                  ? formatNumber(salaryValue, locale, true, 2)
                  : '0,00'}
              </Typography>
              {showHoursWarning(record) && (
                <Typography variant="caption" component="p" color="error">
                  {formatMessage(messages.hoursWarningLabel)}
                </Typography>
              )}
            </>
          );
        },
      },
      ...pendingColumns.filter((column) => column.id === 'adminComment'),
      {
        id: 'actions',
        label: '',
        width: 0,
        align: 'center',
        whiteSpace: 'nowrap',
        render: (_: Object, record: UCM.CandidateJobShiftType) => {
          const userRecord = usersRecords[record.uuid] ?? record;

          return (
            <Grid display="flex" alignItems="center">
              <ConfirmationDialog
                message={formatMessage(
                  messages.approveTimeTrackingConfirmMessage,
                )}
                onConfirm={() => handleApprove(record.uuid, userRecord)}
              >
                {(handleOpen) => (
                  <Tooltip title={formatMessage(messages.approveAndSubmitHint)}>
                    <IconButton onClick={handleOpen}>
                      <CheckOutlinedIcon fontSize="small" color="success" />
                    </IconButton>
                  </Tooltip>
                )}
              </ConfirmationDialog>

              <Tooltip title={formatMessage(messages.cancelHint)}>
                <IconButton
                  onClick={() => handleCancelAndUnCancel(record, 'cancel')}
                >
                  <CloseOutlinedIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </Grid>
          );
        },
      },
    ];
  }, [
    usersRecords,
    handleChangeRecord,
    handleCheckInChange,
    handleCheckOutChange,
    filterDeviationReasons,
  ]);

  const cancelledColumns = useMemo(() => {
    const excludedColumns = ['actions'];

    return [
      ...submittedColumns.filter(
        (column) => !excludedColumns.includes(column.id),
      ),
      {
        id: 'actions',
        label: '',
        width: 0,
        align: 'center',
        whiteSpace: 'nowrap',
        render: (_: Object, record: UCM.CandidateJobShiftType) => (
          <Grid display="flex" alignItems="center">
            <Tooltip title={formatMessage(messages.unCancelHint)}>
              <IconButton
                onClick={() => handleCancelAndUnCancel(record, 'unCancel')}
              >
                <UndoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        ),
      },
    ];
  }, [pendingColumns, submittedColumns]);

  const doneColumns = useMemo(() => {
    const excludedColumns = ['actions'];

    return [
      ...submittedColumns.filter(
        (column) => !excludedColumns.includes(column.id),
      ),
    ];
  }, [submittedColumns]);

  const columns: Record<
    UCM.CandidateJobShiftStatusType,
    Array<{
      id: string;
      label: string | React.ReactNode;
    }>
  > = useMemo(
    () => ({
      done: doneColumns,
      upcoming: upcomingColumns,
      approved: submittedColumns,
      submitted: submittedColumns,
      cancelled: cancelledColumns,
      check_time_available: pendingColumns,
    }),
    [
      doneColumns,
      pendingColumns,
      upcomingColumns,
      submittedColumns,
      cancelledColumns,
    ],
  );

  const handleActiveTabChange = useCallback(
    (tab: UCM.CandidateJobShiftStatusType) => {
      setActiveTab(tab);
      setParams({ page: 1 });
      setUsersRecords({});
      setFilters((prevFilters) => ({
        ...prevFilters,
        status: tab,
      }));
    },
    [],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const handleSearch = useCallback((filters: CandidateJobShiftFiltersType) => {
    setParams({ page: 1 });
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...filters,
    }));
  }, []);

  const handleDateRangeSave = useCallback(
    (startDate: Moment | null, endDate: Moment | null) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        from_date: startDate,
        to_date: endDate,
      }));
    },
    [],
  );

  const closeCancellation = useCallback(() => {
    setOpenCancellation(false);
    setSelected(null);
    setCancellationReason(null);
    setEditCancellation(null);
  }, []);

  const handleSave = useCallback(
    async (uuid: string, record: UserRecordType) => {
      saveTimeTracking({
        record,
        timeTrackingUuid: uuid,
      });
    },
    [],
  );

  const handleApprove = useCallback(
    async (uuid: string, record: UserRecordType) => {
      approveTimeTracking({
        record,
        timeTrackingUuid: uuid,
      });
    },
    [],
  );

  const handleCancellation = useCallback(async (data: CancellationData) => {
    const { type, ...payload } = data;

    const filteredData = Object.fromEntries(
      Object.entries(payload).filter(
        ([_, value]) => value != null && value !== '',
      ),
    );

    return new Promise<boolean>((resolve) => {
      if (type === 'save') {
        cancelTimeTracking(
          { ...filteredData },
          {
            onSuccess: () => {
              closeCancellation();
              resolve(true);
            },
            onError: () => resolve(false),
          },
        );
      } else if (type === 'withdraw') {
        withdrawCancellationNotices({
          cancellationUuid: payload.cancellationUuid,
          reason_code: payload.reason_code,
        });
      }
    });
  }, []);

  const handleImport = useCallback((file: File, onSuccess: () => void) => {
    importTimeTracking(file, { onSuccess });
  }, []);

  const loading = useMemo(() => {
    return isFetchingCandidateJobShifts || isImporting;
  }, [isFetchingCandidateJobShifts, isImporting]);

  return (
    <PageLayout
      tabs={navTabs}
      activeTab={activeTab}
      iconName="time_tracking"
      title={formatMessage(messages.title)}
      onActiveTabChange={handleActiveTabChange}
    >
      <Grid container spacing={2} mt={1}>
        <Grid
          gap={2}
          display="flex"
          size={{ xs: 12, md: 8 }}
          flexDirection={{ xs: 'column', md: 'column', lg: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'flex-start', lg: 'center' }}
        >
          <Grid size={12}>
            <SearchFilterBar
              variant="noBorder"
              fields={searchFilters}
              onSearch={handleSearch as any} // eslint-disable-line
            />
          </Grid>

          <Grid size={12}>
            <DateRangeModal onSave={handleDateRangeSave} />
          </Grid>
        </Grid>

        <Grid container size={{ xs: 'auto', md: 4 }} alignItems="center">
          <Grid size={12} display="flex" justifyContent="flex-end">
            <ImportButton importing={isImporting} onImport={handleImport} />
          </Grid>
        </Grid>

        <Grid size={12}>
          <DataTable
            loading={loading}
            columns={columns[activeTab]}
            onPaginate={handlePagination}
            rows={candidateJobShifts?.data}
            pagination={candidateJobShifts?.meta}
          />
        </Grid>

        <CancellationModal
          open={openCancellation}
          isLoading={isCancelling}
          candidate={selected?.user}
          type={cancellationReason!}
          onClose={closeCancellation}
          onConfirm={handleCancellation}
          cancellation={editCancellation}
          jobId={selected?.jobId.toString() ?? ''}
          disabledButton={!selected || isCancelling}
          enabledFetch={Boolean(selected) && Boolean(cancellationReason)}
        />
        <PaymentSummaryFormModal
          tab={activeTab}
          open={openPaymentModal}
          paymentRecord={paymentRecord}
          onChange={handleChangeRecord}
          onClose={handleClosePaymentModal}
          localRecord={paymentRecord && usersRecords[paymentRecord.uuid]}
        />
      </Grid>
    </PageLayout>
  );
}
function formatDeviation(minutes: string, status: string | null | undefined) {
  const minutesNum = parseInt(minutes, 10);
  if (!minutesNum || minutesNum === 0) return 'On time';
  let hours = Math.floor(minutesNum / 60);
  let mins = minutesNum % 60;
  const hoursText = hours ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : null;
  const minsText = mins ? `${mins} ${mins === 1 ? 'min' : 'mins'}` : null;
  const deviation = compact([hoursText, minsText]).join(' ');
  return deviation && status
    ? compact([deviation, status]).join(' ')
    : deviation || '';
}

function showHoursWarning(record: UCM.CandidateJobShiftType) {
  return record?.billableHours + record?.breakHours > WORKED_HOURS_WARNING;
}
